import { Link } from "react-router-dom";
import { useTenant } from "../../Contexts/TenantContext";
import { toPublicPath } from "../../Utils/publicRoutes";
import { formatPhoneNumber, formatPostalCode } from "../../Utils/DataFormatters";
import visaIcon from "../../assets/payments/visa.svg";
import mastercardIcon from "../../assets/payments/mastercard.svg";
import amexIcon from "../../assets/payments/amex.svg";
import eloIcon from "../../assets/payments/elo.svg";
import bankingBilletLogo from "../../assets/payments/banking-billet.png";
import pixLogo from "../../assets/payments/pix.png";
import sslBadge from "../../assets/payments/ssl-badge.png";
import googleSafe from "../../assets/payments/google-safe.png";
import SocialMediaIcons from "../SocialMediaIcons/SocialMediaIcons";
import FooterInfo from "../FooterInfo/FooterInfo";

const Footer = () => {
  const { tenantContact, tenantBranches, tenantPreference } = useTenant();
  const badgeClass = "bg-white rounded shadow-sm p-1.5 sm:p-2 border border-gray-200";

  return (
    <footer className="bg-neutral-900 text-white">
      <div className="max-w-[94rem] mx-auto grid grid-cols-1 md:grid-cols-3 gap-10 px-6 py-10 sm:gap-8 sm:py-20 md:gap-20">
        <div>
          <h3 className="font-bold mb-2 sm:mb-10">{"INFORMAÇÕES"}</h3>
          {tenantContact?.phoneNumber &&
            <div>
              <p className="mb-1 sm:mb-2 text-gray-300">{"Contato"}</p>
              <p className="text-primary-600 text-xl md:text-2xl font-bold mb-1 sm:mb-2">{formatPhoneNumber(tenantContact?.phoneNumber)}</p>
            </div>
          }
          {tenantContact?.whatsAppNumber && (
            <div>
              <p className="mb-1 sm:mb-2 text-gray-300">WhatsApp</p>
              <p className="text-primary-600 text-xl md:text-2xl font-bold mb-1 sm:mb-2">
                {formatPhoneNumber(tenantContact.whatsAppNumber)}
              </p>
            </div>
          )}
          <p className="mb-1 sm:mb-2 text-gray-300">{tenantContact?.email}</p>
          {tenantContact?.street && tenantContact?.number && tenantContact?.neighborhood && tenantContact?.city && tenantContact?.state && tenantContact?.postalCode &&
            <p className="text-sm md:text-base text-white">
              {`${tenantContact.street}, ${tenantContact.number} - ${tenantContact.neighborhood}, ${tenantContact.city}/${tenantContact.state} - ${formatPostalCode(tenantContact.postalCode)}${
                tenantContact?.complement ? ` - ${tenantContact.complement}` : ""
              }`}
            </p>
          }
          {tenantBranches?.map(branch => (
            <p key={branch.id} className="mt-1 text-sm md:text-base text-white">
              {`${branch.street}, ${branch.number} - ${branch.neighborhood}, ${branch.city}/${branch.state} - ${formatPostalCode(branch.postalCode)}${branch.complement ? ` - ${branch.complement}` : ""}`}
            </p>
          ))}
        </div>

        <div>
          <h3 className="font-bold mb-2 sm:mb-10">{"MAPA DO SITE"}</h3>
          <ul className="space-y-1">
            <li><Link to="/" className="hover:text-primary-600">{"Início"}</Link></li>
            <li><Link to={toPublicPath("/sobre")} className="hover:text-primary-600">{"A Empresa"}</Link></li>
            <li><Link to={toPublicPath("/produtos")} className="hover:text-primary-600">{"Produtos"}</Link></li>
            {tenantPreference?.enableTechnicalAssistancePage && (
              <li><Link to={toPublicPath("/assistencia-tecnica")} className="hover:text-primary-600">{"Assistência Técnica"}</Link></li>
            )}
            <li><Link to={toPublicPath("/contato")} className="hover:text-primary-600">{"Contato"}</Link></li>
            <li><Link to={toPublicPath("/politicas")} className="hover:text-primary-600">{"Políticas"}</Link></li>
            <li><Link to={toPublicPath("/politica-privacidade")} className="hover:text-primary-600">{"Políticas de Privacidade"}</Link></li>
            <li><Link to={toPublicPath("/politica-trocas")} className="hover:text-primary-600">{"Políticas de Trocas e Devoluções"}</Link></li>
            <li><Link to={toPublicPath("/politica-cookies")} className="hover:text-primary-600">{"Políticas de Cookies"}</Link></li>
            <li><Link to={toPublicPath("/faq")} className="hover:text-primary-600">FAQ</Link></li>
          </ul>
        </div>

        <div>
          <h3 className="font-bold mb-2 sm:mb-10">{"ONDE ESTAMOS"}</h3>
          {(() => {
            const branchesWithAddress = tenantBranches?.filter(
              (b) => b.street && b.number && b.neighborhood && b.city && b.state
            ) ?? [];
            const hasContactAddress = !!(tenantContact?.street && tenantContact?.number && tenantContact?.neighborhood && tenantContact?.city && tenantContact?.state);
            const totalAddresses = (hasContactAddress ? 1 : 0) + branchesWithAddress.length;
            const useGrid = totalAddresses >= 3;

            const mapHeight = totalAddresses === 1 ? "200" : "160";
            const mapWrapperClass = `rounded-xl overflow-hidden shadow-lg${!useGrid && totalAddresses > 1 ? " max-w-[280px]" : ""}`;
            const containerClass = useGrid ? "grid grid-cols-1 md:grid-cols-2 gap-2" : "flex flex-col gap-3";

            type MapEntry = { key: string; label: string | null; query: string };
            const maps: MapEntry[] = [];
            if (hasContactAddress) {
              maps.push({
                key: "contact",
                label: null,
                query: `${tenantContact!.street}, ${tenantContact!.number}, ${tenantContact!.neighborhood}, ${tenantContact!.city}, ${tenantContact!.state}`,
              });
            }
            branchesWithAddress.forEach((branch) => {
              maps.push({
                key: String(branch.id),
                label: branch.label ?? null,
                query: `${branch.street}, ${branch.number}, ${branch.neighborhood}, ${branch.city}, ${branch.state}`,
              });
            });

            return (
              <div className={containerClass}>
                {maps.map((m) => (
                  <div key={m.key}>
                    {m.label && <p className="text-sm text-gray-300 mb-1">{m.label}</p>}
                    <div className={mapWrapperClass}>
                      <iframe
                        title={m.label ?? "Localização da empresa"}
                        src={`https://www.google.com/maps?q=${encodeURIComponent(m.query)}&output=embed`}
                        width="100%"
                        height={mapHeight}
                        allowFullScreen={true}
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                      ></iframe>
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      </div>

      <div className="py-6 sm:py-8 border-t border-neutral-800">
        <div className="max-w-[94rem] mx-auto px-6 grid grid-cols-2 gap-4 items-start sm:flex sm:items-center sm:justify-between sm:gap-6">
          <div className="sm:w-auto">
            <p className="font-bold mb-2 sm:mb-3">{"Pague com"}</p>
            <div className="flex flex-col gap-2 sm:gap-3">
              <div className="flex flex-wrap gap-2 sm:gap-4 items-center">
                <img src={visaIcon} alt="Visa" className="h-5 sm:h-[26px]" />
                <img src={mastercardIcon} alt="Mastercard" className="h-5 sm:h-[26px]" />
                <img src={amexIcon} alt="Amex" className="h-5 sm:h-[26px]" />
                <img src={eloIcon} alt="Elo" className="h-5 sm:h-[26px]" />
              </div>
              <div className="flex flex-nowrap gap-2 sm:gap-4 sm:flex-wrap items-center">
                <div className={badgeClass}><img src={bankingBilletLogo} alt="Boleto Bancário" className="h-5 w-auto sm:h-[25px]" /></div>
                <div className={badgeClass}><img src={pixLogo} alt="PIX" className="h-5 w-auto sm:h-[25px]" /></div>
              </div>
            </div>
          </div>

          <div className="sm:w-auto sm:ml-0 sm:mt-0 sm:self-auto">
            <p className="font-bold mb-2 sm:mb-3">{"Selos"}</p>
            <div className="grid grid-cols-2 gap-2 sm:flex sm:gap-4 sm:items-center">
              <div className={badgeClass}><img src={sslBadge} alt="Site Protegido SSL" className="h-5 sm:h-10" /></div>
              <div className={badgeClass}><img src={googleSafe} alt="Google Safe Browsing" className="h-5 sm:h-10" /></div>
            </div>
          </div>
        </div>
      </div>

      <SocialMediaIcons className="bg-neutral-700 p-14 flex items-center justify-center space-x-6 text-3xl text-white" />
      <FooterInfo />
    </footer>
  );
};

export default Footer;
